import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Copy, Download, Eye, FileMinus2, Mail, MoreVertical, Plus, Search, Trash2, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AppShell from "../components/AppShell";
import CancelInvoiceModal from "../components/CancelInvoiceModal";
import CreditNoteModal from "../components/CreditNoteModal";
import ExportMenu from "../components/ExportMenu";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });

const STATUS_META = {
  paid: { label: "Pagada", color: "#238a65", bg: "#dcf7ed" },
  pending: { label: "Pendiente", color: "#a97a11", bg: "#fff2c9" },
  sent: { label: "Enviada", color: "#4169e1", bg: "#eef4ff" },
  draft: { label: "Borrador", color: "#7c3aed", bg: "#f3e8ff" },
  cancelled: { label: "Cancelada", color: "#d7565d", bg: "#ffe5e6" },
  overdue: { label: "Vencida", color: "#283237", bg: "#e6e8ea" },
};

const FILTERS = [
  ["all", "Todas"], ["today", "Hoy"], ["week", "Semana"], ["month", "Mes"], ["year", "Año"],
];
const STATUSES = [
  ["", "Cualquier estado"], ["paid", "Pagadas"], ["pending", "Pendientes"], ["overdue", "Vencidas"], ["cancelled", "Canceladas"],
];

export default function SentInvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [creditNoteTarget, setCreditNoteTarget] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [listRes, summaryRes] = await Promise.all([
        api.get("/invoices/sent", { params: { q, filter, status }, withCredentials: true }),
        api.get("/invoices/sent/summary", { withCredentials: true }),
      ]);
      setInvoices(listRes.data);
      setSummary(summaryRes.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar las facturas.");
    }
  }, [q, filter, status]);

  useEffect(() => { const t = setTimeout(refresh, 200); return () => clearTimeout(t); }, [refresh]);

  const toggleSelect = (id) => setSelected((c) => { const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(invoices.map((i) => i.id)));

  const bulk = async (action) => {
    if (!selected.size) return;
    try {
      const r = await api.post("/invoices/sent/bulk-action", { ids: [...selected], action }, { withCredentials: true });
      setNotice(`Acción aplicada a ${Object.values(r.data)[0]} factura(s).`);
      clearSelection();
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos completar la acción."); }
  };

  const markStatus = async (invoice, newStatus) => {
    try {
      await api.put(`/invoices/sent/${invoice.id}`, { status: newStatus }, { withCredentials: true });
      setNotice(`Factura ${STATUS_META[newStatus]?.label.toLowerCase()}.`);
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos actualizar."); }
  };

  const duplicate = async (invoice) => {
    try {
      await api.post(`/invoices/sent/${invoice.id}/duplicate`, {}, { withCredentials: true });
      setNotice("Factura duplicada como borrador.");
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos duplicar."); }
  };

  const remove = async (invoice) => {
    if (!window.confirm(`¿Eliminar borrador ${invoice.folio}?`)) return;
    try {
      await api.delete(`/invoices/sent/${invoice.id}`, { withCredentials: true });
      setNotice("Borrador eliminado.");
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos eliminar."); }
  };

  const kpis = summary || { total_invoiced: 0, paid: 0, pending: 0, overdue: 0, average_amount: 0, average_days_until_payment: 0 };
  const monthlyData = summary?.monthly || [];
  const topClients = summary?.top_clients || [];
  const pieData = summary ? [
    { name: "Pagadas", value: summary.paid_vs_pending.paid_count, color: "#238a65" },
    { name: "Pendientes", value: summary.paid_vs_pending.pending_count, color: "#f7c34e" },
  ] : [];

  return (
    <AppShell>
      <main className="page invoices-sent-page">
        <header className="expenses-head">
          <div>
            <small className="eyebrow">FACTURAS ENVIADAS</small>
            <h1>Cobros, <em>bajo control.</em></h1>
          </div>
          <div className="expenses-head-actions">
            <ExportMenu resource="cancellations" testid="cancellations-export" label="Reporte cancelaciones" />
            <button className="expenses-add-button" data-testid="new-invoice-cta" onClick={() => navigate("/facturacion")}>
              <Plus size={17} />
              Nueva factura
            </button>
          </div>
        </header>

        {notice && <p className="expenses-notice" data-testid="sent-notice">{notice}</p>}
        {error && <p className="expenses-error" data-testid="sent-error">{error}</p>}

        <section className="expenses-summary" data-testid="sent-kpis">
          <article><small>TOTAL FACTURADO</small><strong>{money.format(kpis.total_invoiced)}</strong></article>
          <article><small>PAGADO</small><strong style={{ color: "#238a65" }}>{money.format(kpis.paid)}</strong></article>
          <article><small>PENDIENTE</small><strong style={{ color: "#a97a11" }}>{money.format(kpis.pending)}</strong></article>
          <article><small>VENCIDO</small><strong style={{ color: "#d7565d" }}>{money.format(kpis.overdue)}</strong></article>
          <article><small>PROMEDIO</small><strong>{money.format(kpis.average_amount)}</strong><span>Días al pago: {kpis.average_days_until_payment}</span></article>
        </section>

        <section className="sent-charts">
          <article data-testid="chart-monthly-revenue">
            <b>Ingresos mensuales</b>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f4" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#4169e1" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </article>
          <article data-testid="chart-monthly-count">
            <b>Facturas emitidas por mes</b>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f4" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="count" fill="#28a878" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </article>
          <article data-testid="chart-paid-vs-pending">
            <b>Pagadas vs Pendientes</b>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} innerRadius={40} outerRadius={70} paddingAngle={4} dataKey="value">
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </article>
          <article data-testid="chart-top-clients">
            <b>Top clientes</b>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={topClients} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f4" />
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="name" type="category" fontSize={11} width={90} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Bar dataKey="total" fill="#ef6670" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </article>
        </section>

        <section className="expenses-controls">
          <label className="expenses-search">
            <Search size={17} />
            <input data-testid="sent-search" placeholder="Buscar por cliente, RFC, folio, UUID o monto" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="expenses-period" data-testid="sent-filter">
            {FILTERS.map(([k, l]) => <button key={k} className={filter === k ? "active" : ""} onClick={() => setFilter(k)} data-testid={`filter-${k}`}>{l}</button>)}
          </div>
          <select data-testid="sent-status-filter" className="expenses-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </section>

        {selected.size > 0 && (
          <div className="vault-bulk-bar" data-testid="sent-bulk-bar">
            <b>{selected.size} seleccionada{selected.size === 1 ? "" : "s"}</b>
            <button onClick={() => bulk("mark_paid")} data-testid="bulk-mark-paid">Marcar pagadas</button>
            <button onClick={() => bulk("send_email")} data-testid="bulk-send"><Mail size={14} />Enviar</button>
            <button onClick={() => bulk("archive")} data-testid="bulk-archive"><Archive size={14} />Archivar</button>
            <button onClick={() => bulk("delete_drafts")} data-testid="bulk-delete" className="danger"><Trash2 size={14} />Borradores</button>
            <button onClick={clearSelection}>Cancelar</button>
          </div>
        )}

        {invoices.length === 0 ? (
          <div className="expenses-empty" data-testid="sent-empty">
            <img src="/mi-contador-mark.png" alt="" className="empty-brand-mark" aria-hidden="true" />
            <h2>Aún no tienes facturas enviadas.</h2>
            <p>Crea tu primera factura para verla aquí.</p>
            <button onClick={() => navigate("/facturacion")}>Crear factura</button>
          </div>
        ) : (
          <div className="expenses-table-wrap">
            <table className="expenses-table" data-testid="sent-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}><input type="checkbox" checked={selected.size === invoices.length && invoices.length > 0} onChange={(e) => (e.target.checked ? selectAll() : clearSelection())} data-testid="sent-select-all" /></th>
                  <th>Folio</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>IVA</th>
                  <th>Estado</th>
                  <th>PDF</th>
                  <th>XML</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const meta = STATUS_META[inv.computed_status] || STATUS_META.sent;
                  return (
                    <tr key={inv.id} data-testid={`sent-row-${inv.id}`}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggleSelect(inv.id)} data-testid={`sent-select-${inv.id}`} />
                      </td>
                      <td style={{ cursor: "pointer" }} onClick={() => navigate(`/facturas/${inv.id}`)}>
                        <b>{inv.folio}</b>
                        <small>{(inv.id || "").slice(0, 8).toUpperCase()}</small>
                      </td>
                      <td>
                        <b>{inv.receiver_name}</b>
                        <small>{inv.receiver_rfc}</small>
                      </td>
                      <td>{(inv.created_at || "").slice(0, 10)}</td>
                      <td><strong>{money.format(Number(inv.total || 0))}</strong></td>
                      <td>{money.format(Number(inv.iva || 0))}</td>
                      <td><span className="sent-status" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span></td>
                      <td>{inv.facturapi_invoice_id ? "✓" : "—"}</td>
                      <td>{inv.xml_url ? "✓" : "—"}</td>
                      <td className="expenses-actions-cell">
                        <SentRowMenu invoice={inv} onOpen={() => navigate(`/facturas/${inv.id}`)} onMarkPaid={() => markStatus(inv, "paid")} onMarkPending={() => markStatus(inv, "pending")} onCancel={() => setCancelTarget(inv)} onCreditNote={() => setCreditNoteTarget(inv)} onDuplicate={() => duplicate(inv)} onDelete={() => remove(inv)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {cancelTarget && (
          <CancelInvoiceModal
            invoice={cancelTarget}
            onClose={() => setCancelTarget(null)}
            onCancelled={() => {
              setCancelTarget(null);
              setNotice("Factura cancelada. El CFDI queda pendiente de confirmación SAT.");
              refresh();
            }}
          />
        )}

        {creditNoteTarget && (
          <CreditNoteModal
            invoice={creditNoteTarget}
            onClose={() => setCreditNoteTarget(null)}
            onCreated={(note) => {
              setCreditNoteTarget(null);
              setNotice(`Nota de crédito ${note.folio} emitida.`);
              refresh();
            }}
          />
        )}
      </main>
    </AppShell>
  );
}

function SentRowMenu({ invoice, onOpen, onMarkPaid, onMarkPending, onCancel, onCreditNote, onDuplicate, onDelete }) {
  const [open, setOpen] = useState(false);
  const isCancelled = invoice.computed_status === "cancelled";
  const isDraft = invoice.computed_status === "draft";
  return (
    <div className="vault-card-menu" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <button data-testid={`sent-menu-${invoice.id}`} onClick={() => setOpen((v) => !v)} aria-label="Acciones"><MoreVertical size={16} /></button>
      {open && (
        <ul data-testid={`sent-menu-list-${invoice.id}`}>
          <li onClick={() => { onOpen(); setOpen(false); }}><Eye size={14} />Abrir factura</li>
          {!isCancelled && !isDraft && (
            <li onClick={() => { onMarkPaid(); setOpen(false); }} data-testid={`sent-mark-paid-${invoice.id}`}>Marcar como pagada</li>
          )}
          {!isCancelled && !isDraft && (
            <li onClick={() => { onMarkPending(); setOpen(false); }}>Marcar como pendiente</li>
          )}
          {!isCancelled && !isDraft && (
            <li onClick={() => { onCreditNote(); setOpen(false); }} data-testid={`sent-credit-note-${invoice.id}`}><FileMinus2 size={14} />Emitir nota de crédito</li>
          )}
          {!isCancelled && !isDraft && (
            <li onClick={() => { onCancel(); setOpen(false); }} data-testid={`sent-cancel-cfdi-${invoice.id}`} className="danger"><XCircle size={14} />Cancelar CFDI</li>
          )}
          <li onClick={() => { onDuplicate(); setOpen(false); }}><Copy size={14} />Duplicar</li>
          {isDraft && <li onClick={() => { onDelete(); setOpen(false); }} className="danger"><Trash2 size={14} />Eliminar borrador</li>}
        </ul>
      )}
    </div>
  );
}
